import { generatePath, matchPath } from "react-router";
import { allRoutes } from ".";

const APP_ROUTE: unique symbol = Symbol("app-route");

type RouteParams = Record<string, string | undefined>;
type PathQueryValue = string | null | undefined;
type PathOptions = {
  query?: Record<string, PathQueryValue>;
  hash?: string | null | undefined;
};

export type AppRoute<TParams extends RouteParams = {}> = {
  readonly [APP_ROUTE]: true;
  pattern: string;
  meta: RouteMeta;
  path: RoutePath<TParams>;
  match: (pathname: string | null | undefined) => boolean;
};

type RouteTree = {
  readonly [key: string]: AppRoute<any> | RouteTree;
};

export type RouteMeta = {
  header: boolean;
  footer: "default" | "small" | false;
  tools: boolean;
};

export const DEFAULT_META = {
  header: true,
  footer: "default",
  tools: false,
} satisfies RouteMeta;

export function defineRoute<TParams extends RouteParams = {}>(
  pattern: string,
  meta: Partial<RouteMeta> = {},
  buildPattern = pattern
): AppRoute<TParams> {
  return {
    [APP_ROUTE]: true,
    pattern,
    meta: { ...DEFAULT_META, ...meta },
    path: createRoutePath<TParams>(buildPattern),
    match: pathname => {
      if (!pathname) {
        return false;
      }
      return Boolean(matchPath({ path: pattern, end: true }, pathname));
    },
  };
}

// `path` supports two shapes:
// `path(params?)` for static routes and routes whose params are all optional,
// and `path(params)` when any param is required.
type RoutePath<TParams extends RouteParams> =
  HasRequiredKeys<TParams> extends true
    ? (params: TParams, options?: PathOptions) => string
    : (params?: TParams, options?: PathOptions) => string;

type HasRequiredKeys<TParams extends RouteParams> = {} extends TParams ? false : true;

// `TParams` appears once, which reads as inert, but it is what every route's
// `path()` is typed by; its constraint would leave them all untyped.
const createRoutePath =
  <TParams extends RouteParams>(pattern: string) =>
  (params?: TParams, options?: PathOptions) => {
    const path = generatePath(pattern, params ?? {});
    return appendHash(appendQuery(path, options?.query), options?.hash);
  };

export const flattenRoutes = (value: AppRoute<any> | RouteTree): AppRoute<any>[] =>
  APP_ROUTE in value ? [value] : Object.values(value).flatMap(flattenRoutes);

export type ExtractParams<TRoute extends AppRoute<any>> =
  TRoute extends AppRoute<infer TParams> ? TParams : never;

function appendQuery(path: string, query?: Record<string, PathQueryValue>) {
  if (!query) {
    return path;
  }

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value) {
      searchParams.append(key, value);
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `${path}?${queryString}` : path;
}

function appendHash(path: string, hash?: string | null) {
  if (!hash) {
    return path;
  }

  return `${path}${hash.startsWith("#") ? hash : `#${hash}`}`;
}

export function findRouteByPath(path: string | null | undefined) {
  if (!path) {
    return null;
  }

  const exactMatch = allRoutes.find(route => routeEqualsPath(route.pattern, path));
  if (exactMatch) {
    return exactMatch;
  }

  return allRoutes.find(route => matchesWildcardPattern(route.pattern, path)) ?? null;
}

// Route lookup intentionally reuses Talaera's older segment-based equality semantics
// before falling back to `/*` routes. This preserves historical behavior where broad
// wildcard routes like `/admin/*` do not shadow more specific routes such as
// `/admin/content` when resolving route metadata.
function routeEqualsPath(pattern: string, path: string) {
  const getPathSegments = (p: string) => p.split("/").filter(Boolean);
  const pathParts = getPathSegments(path);
  const patternParts = getPathSegments(pattern);

  if (pathParts.length !== patternParts.length && !pattern.includes("?")) {
    return false;
  }

  if (pattern.includes("?")) {
    for (const [index, patternPart] of patternParts.entries()) {
      if (patternPart.startsWith(":") && patternPart.endsWith("?")) {
        pathParts[index] = pathParts[index] ?? "optional";
      }
    }
  }

  for (const [index, pathPart] of pathParts.entries()) {
    const patternPart = patternParts[index];

    if (!patternPart) {
      return false;
    }

    if (pathPart.startsWith(":") || patternPart.startsWith(":")) {
      continue;
    }

    if (pathPart !== patternPart) {
      return false;
    }
  }

  return true;
}

function matchesWildcardPattern(pattern: string, path: string) {
  if (!pattern.endsWith("/*")) {
    return false;
  }

  const prefix = pattern.slice(0, -2);
  return path === prefix || path.startsWith(`${prefix}/`);
}
